<template>
  <div class="phd-demo-wrap">
    <!-- Quiet inline trigger — single small link, no cart-like card. The
         conversion off-ramp is still here for buyers who want a demo, but
         it doesn't compete with the actual cart for visual weight. -->
    <button
      type="button"
      class="phd-demo-trigger"
      @click="open"
    >
      <span class="phd-demo-trigger-icon" aria-hidden="true">📅</span>
      <span>Want a quick walkthrough? <strong>Schedule a demo</strong></span>
    </button>

    <!-- Modal — uses the same ph-modal markup so it lives next to the
         checkout modal visually. Trapped focus, esc-to-close, click-out
         to dismiss when not submitting. -->
    <transition name="ph-modal-fade">
      <div
        v-if="modalOpen"
        ref="backdrop"
        class="ph-modal-backdrop"
        @click.self="close"
        @keydown.esc.stop="close"
      >
        <div
          class="ph-modal phd-demo-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="phd-demo-title"
          tabindex="-1"
          ref="modal"
        >
          <button
            type="button"
            class="ph-modal-close"
            @click="close"
            aria-label="Close"
            :disabled="submitting"
          >×</button>
          <header class="ph-modal-header">
            <span class="ph-modal-eyebrow">Book a demo</span>
            <h2 id="phd-demo-title">Pick a time that works.</h2>
            <p>15 minutes, on us. We'll walk through the dashboard, your team setup, and any questions.</p>
          </header>

          <form class="ph-modal-form" @submit.prevent="submit">
            <div class="ph-field-row">
              <label class="ph-field">
                <span class="ph-field-label">First name *</span>
                <input
                  type="text"
                  v-model="form.first_name"
                  class="ph-field-input"
                  autocomplete="given-name"
                  required
                />
              </label>
              <label class="ph-field">
                <span class="ph-field-label">Last name *</span>
                <input
                  type="text"
                  v-model="form.last_name"
                  class="ph-field-input"
                  autocomplete="family-name"
                  required
                />
              </label>
            </div>

            <label class="ph-field ph-field--full">
              <span class="ph-field-label">Work email *</span>
              <input
                type="email"
                v-model="form.email"
                class="ph-field-input"
                autocomplete="email"
                required
              />
            </label>

            <div class="ph-field-row">
              <label class="ph-field">
                <span class="ph-field-label">Phone *</span>
                <input
                  type="tel"
                  :value="form.phone"
                  @input="onPhoneInput"
                  class="ph-field-input"
                  autocomplete="tel"
                  inputmode="tel"
                  placeholder="(555) 555-5555"
                  required
                />
              </label>
              <label class="ph-field">
                <span class="ph-field-label">Company *</span>
                <input
                  type="text"
                  v-model="form.company"
                  class="ph-field-input"
                  autocomplete="organization"
                  required
                />
              </label>
            </div>

            <label class="ph-field ph-field--full">
              <span class="ph-field-label">Preferred date &amp; time *</span>
              <input
                type="datetime-local"
                v-model="form.date_time"
                :min="minDateTime"
                class="ph-field-input"
                required
              />
              <small class="ph-promo-message">We'll confirm by email within one business day.</small>
            </label>

            <button
              type="submit"
              class="ph-btn ph-btn--checkout ph-modal-cta"
              :disabled="!canSubmit || submitting"
            >
              <span v-if="submitting">Submitting…</span>
              <span v-else>Request demo →</span>
            </button>

            <div v-if="error" class="ph-demo-error" role="alert" aria-live="assertive">{{ error }}</div>
          </form>

          <div v-if="success" class="phd-demo-success" role="status" aria-live="assertive">
            <div class="phd-demo-success-icon" aria-hidden="true">✓</div>
            <strong>Got it.</strong>
            <p>We'll be in touch at <strong>{{ form.email }}</strong> within one business day to confirm.</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import teleportToBody from '../../mixins/teleportToBody';

const API_BASE = (process.env.VUE_APP_API_URL || 'http://localhost:8000/').replace(/\/$/, '');

export default {
  name: 'ScheduleDemoCard',
  mixins: [teleportToBody],

  props: {
    // Parent can prefill from buyer state so a buyer who's already typed
    // their email into the checkout email field doesn't have to retype.
    prefill: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      modalOpen: false,
      submitting: false,
      success: false,
      error: '',
      form: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
        date_time: '',
      },
    };
  },

  computed: {
    canSubmit() {
      const f = this.form;
      const phoneDigits = (f.phone || '').replace(/\D/g, '');
      return !!f.first_name.trim()
        && !!f.last_name.trim()
        && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((f.email || '').trim())
        && phoneDigits.length >= 10
        && !!f.company.trim()
        && !!f.date_time;
    },
    // Floor the picker at "now" rounded to the next quarter hour so a buyer
    // can't request a demo in the past.
    minDateTime() {
      const d = new Date();
      const minutes = Math.ceil(d.getMinutes() / 15) * 15;
      d.setMinutes(minutes, 0, 0);
      // Format as YYYY-MM-DDTHH:MM (local time, what datetime-local expects).
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
  },

  methods: {
    open() {
      this.applyPrefill();
      this.modalOpen = true;
      this.success = false;
      this.error = '';
      this.$nextTick(() => {
        if (this.$refs.modal && typeof this.$refs.modal.focus === 'function') {
          this.$refs.modal.focus();
        }
      });
      this.$emit('opened');
    },

    close() {
      if (this.submitting) return;
      this.modalOpen = false;
    },

    applyPrefill() {
      const p = this.prefill || {};
      if (!this.form.first_name && p.first_name) this.form.first_name = p.first_name;
      if (!this.form.last_name && p.last_name) this.form.last_name = p.last_name;
      if (!this.form.email && p.email) this.form.email = p.email;
      if (!this.form.phone && p.phone) this.form.phone = p.phone;
      if (!this.form.company && p.company) this.form.company = p.company;
    },

    onPhoneInput(e) {
      const raw = (e && e.target && typeof e.target.value === 'string') ? e.target.value : '';
      const digits = raw.replace(/\D/g, '').slice(0, 15);
      if (!digits) { this.form.phone = ''; return; }
      if (digits.length <= 3) { this.form.phone = `(${digits}`; return; }
      if (digits.length <= 6) { this.form.phone = `(${digits.slice(0, 3)}) ${digits.slice(3)}`; return; }
      if (digits.length <= 10) {
        this.form.phone = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        return;
      }
      if (digits.length === 11) {
        this.form.phone = `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        return;
      }
      const cc = digits.slice(0, digits.length - 10);
      const rest = digits.slice(-10);
      this.form.phone = `+${cc} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
    },

    async submit() {
      if (!this.canSubmit || this.submitting) return;
      this.submitting = true;
      this.error = '';
      try {
        await axios.post(`${API_BASE}/user/sendDemoRequest`, {
          first_name: this.form.first_name,
          last_name: this.form.last_name,
          email: this.form.email,
          phone: this.form.phone,
          company: this.form.company,
          date_time: this.form.date_time,
        });
        this.success = true;
        this.$emit('submitted', { ...this.form });
      } catch (e) {
        this.error = e?.response?.data?.message
          || 'Could not submit your request right now. Please try again or email us at support@homeoftraining.com.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<!-- Modal infrastructure: imported globally in main.js so .ph-modal-*
     and .ph-field-* classes cascade into this child component. -->
<style lang="scss" scoped>
/* Tiny inline trigger — quiet by default, the cart still wins the
   visual hierarchy. Sits below the cart's pay button in the cart
   sidebar, no padding-heavy card. */
.phd-demo-wrap {
  margin-top: 10px;
}

.phd-demo-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: transparent;
  border: 0;
  padding: 6px 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  line-height: 18px;
  color: #555;
  transition: color 160ms ease;

  &:hover { color: #fe672c; }
  &:focus-visible {
    outline: 2px solid #fe672c;
    outline-offset: 2px;
    border-radius: 4px;
  }

  strong {
    color: #fe672c;
    font-weight: 700;
  }
}

.phd-demo-trigger-icon {
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
}

/* Modal styling adopts ph-modal tokens via the parent .ph-modal class —
   only specifics for this modal here. */
.phd-demo-modal {
  max-width: 540px;
}

.phd-demo-success {
  text-align: center;
  padding: 24px 8px 8px;

  .phd-demo-success-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 12px;
    border-radius: 50%;
    background: rgba(46, 204, 113, 0.14);
    color: #1f9d55;
    font-size: 24px;
    line-height: 48px;
    font-weight: 800;
  }
  strong {
    display: block;
    font-size: 18px;
    line-height: 22px;
    color: #1a1a1a;
    margin-bottom: 8px;
  }
  p {
    font-size: 14px;
    line-height: 20px;
    color: #555;
    margin: 0;
  }
}
</style>
